import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// 🆕 Feb 2026: pagina "Presenze Mensa" (annuale).
// Import XLS SpreadsheetML 2003 in FULL WIPE (ogni upload sostituisce il precedente).
// Righe numerate → il totale è il numero di pasti consumati.
// Filtro per alunno (nome/cognome/CF).
export default function MensaPresenzePage() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [plesso, setPlesso] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(300);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);
  const reqRef = useRef(0);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/mensa-presenze-annuali/stats");
      setStats(r.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadItems = useCallback(async () => {
    const id = ++reqRef.current;
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/mensa-presenze-annuali", {
        params: { page, limit, ...(search ? { search } : {}), ...(plesso ? { plesso } : {}) },
      });
      if (id !== reqRef.current) return;
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore caricamento");
    } finally {
      if (id === reqRef.current) setLoading(false);
    }
  }, [page, limit, search, plesso]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!window.confirm(`Confermi l'upload di "${f.name}"?\n\nComportamento: i record con la stessa (data, CF) verranno AGGIORNATI (deduplica), gli altri restano.\nNessun dato precedente viene cancellato.`)) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true); setUploadResult(null); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/mensa-presenze-annuali/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000,
      });
      setUploadResult(r.data);
      await loadStats();
      setPage(1);
      await loadItems();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Errore upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Svuotare completamente la tabella Presenze Mensa?\n\nOperazione IRREVERSIBILE.")) return;
    try {
      await api.delete("/mensa-presenze-annuali");
      await loadStats();
      await loadItems();
    } catch (ex) {
      alert(ex.response?.data?.detail || ex.message || "Errore");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const fmtDT = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; }
  };

  return (
    <div style={{ padding: "12px 18px" }}>
      <h2 style={{ margin: "0 0 12px", color: "#6a1b9a" }}>🍽️ Presenze Mensa (annuale)</h2>

      {/* Card stats + upload */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 10, marginBottom: 12,
      }}>
        <StatCard label="Totale pasti (righe)" value={(stats?.total || 0).toLocaleString("it-IT")} color="#6a1b9a" testid="mp-stat-total" />
        <StatCard label="Alunni distinti" value={(stats?.distinct_alunni || 0).toLocaleString("it-IT")} color="#00838f" testid="mp-stat-alunni" />
        <StatCard label="Prima data" value={stats?.data_min || "—"} color="#1b5e20" testid="mp-stat-min" />
        <StatCard label="Ultima data" value={stats?.data_max || "—"} color="#c62828" testid="mp-stat-max" />
        <StatCard label="Ultimo import" value={stats?.uploaded_at ? fmtDT(stats.uploaded_at) : "—"} color="#ef6c00" testid="mp-stat-upload" />
      </div>

      {/* Toolbar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: 12, background: "#fafafa", border: "1px solid #ddd", borderRadius: 4, marginBottom: 12,
      }}>
        <label style={{
          padding: "8px 16px", background: uploading ? "#888" : "#6a1b9a", color: "white",
          fontSize: 12, fontWeight: 800, cursor: uploading ? "wait" : "pointer", borderRadius: 3,
        }}>
          {uploading ? "⏳ Import in corso..." : "📤 Carica XLS (upsert: aggiorna doppioni)"}
          <input ref={fileRef} type="file" accept=".xls,.xml,application/vnd.ms-excel" onChange={onFileChange}
            data-testid="mp-upload-input" disabled={uploading} style={{ display: "none" }} />
        </label>
        <span style={{ fontSize: 11, color: "#666" }}>
          Le importazioni successive <b>aggiornano i doppioni</b> (stessa data+CF); il resto viene mantenuto.
        </span>
        <div style={{ flex: 1 }} />
        <input type="text" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          data-testid="mp-search"
          placeholder="🔎 Filtra per Cognome, Nome o CF..."
          style={{ padding: "6px 10px", border: "1px solid #b0bec5", fontSize: 12, width: 280, borderRadius: 3 }} />
        <select value={plesso} onChange={e => { setPlesso(e.target.value); setPage(1); }}
          data-testid="mp-plesso"
          style={{ padding: "6px 10px", border: "1px solid #b0bec5", fontSize: 12, borderRadius: 3 }}>
          <option value="">Tutti i plessi</option>
          <option value="BS1A00700E">A — Infanzia (BS1A00700E)</option>
          <option value="BS1E00100D">E — Primaria E (BS1E00100D)</option>
          <option value="BS1E002009">F — Primaria F (BS1E002009)</option>
          <option value="BS1M01400R">M — Secondaria (BS1M01400R)</option>
        </select>
        {stats?.total > 0 && (
          <button onClick={clearAll} data-testid="mp-clear"
            style={{ padding: "6px 12px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            🗑 Svuota tutto
          </button>
        )}
      </div>

      {/* Feedback upload */}
      {uploadResult && (
        <div data-testid="mp-upload-result"
          style={{ padding: 10, marginBottom: 10, background: "#e8f5e9", color: "#1b5e20", border: "1px solid #a5d6a7", fontSize: 12, borderRadius: 3 }}>
          ✅ Import completato:
          {" "}<b>{(uploadResult.inserted || 0).toLocaleString("it-IT")}</b> nuove righe
          {(uploadResult.updated || 0) > 0 && <> · <b>{uploadResult.updated.toLocaleString("it-IT")}</b> aggiornate (doppioni)</>}
          {(uploadResult.skipped || 0) > 0 && <> · {uploadResult.skipped} righe scartate</>}
        </div>
      )}
      {err && (
        <div style={{ padding: 10, marginBottom: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", fontSize: 12, borderRadius: 3 }}>{err}</div>
      )}

      {/* Totale filtrato + paginazione */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, fontSize: 12 }}>
        <div data-testid="mp-total-filtered">
          {loading ? "Caricamento..." : (
            <>
              <b style={{ color: "#6a1b9a" }}>{total.toLocaleString("it-IT")}</b> pasti trovati
              {(search || plesso) && <span style={{ color: "#888", marginLeft: 6 }}>(filtri attivi)</span>}
            </>
          )}
        </div>
        {totalPages > 1 && (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              data-testid="mp-page-prev"
              style={{ padding: "3px 10px", cursor: page <= 1 ? "not-allowed" : "pointer" }}>
              ‹ Prec
            </button>
            <span>Pagina <b>{page}</b> di <b>{totalPages}</b></span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              data-testid="mp-page-next"
              style={{ padding: "3px 10px", cursor: page >= totalPages ? "not-allowed" : "pointer" }}>
              Succ ›
            </button>
          </div>
        )}
      </div>

      {/* Tabella */}
      <div style={{ maxHeight: "60vh", overflowY: "auto", border: "1px solid #ddd", borderRadius: 3 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead style={{ position: "sticky", top: 0, background: "#f3e5f5", zIndex: 1 }}>
            <tr>
              <th style={{ width: 60, padding: "6px 8px", textAlign: "right" }}>#</th>
              <th style={{ width: 90, padding: "6px 8px", textAlign: "left" }}>Data</th>
              <th style={{ width: 90, padding: "6px 8px", textAlign: "left" }}>Plesso</th>
              <th style={{ padding: "6px 8px", textAlign: "left" }}>Studente</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 140 }}>Codice Fiscale</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 90 }}>Classe</th>
              <th style={{ padding: "6px 8px", textAlign: "center", width: 60 }}>Mensa</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 120 }}>Dieta</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: "#888" }}>
                {stats?.total === 0
                  ? "Nessun dato presente. Carica il file XLS per iniziare."
                  : "Nessun risultato per i filtri impostati."}
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={`${it.data_iso}-${it.codice_fiscale}-${it.numero}`}
                data-testid={`mp-row-${it.numero}`}
                style={{ borderTop: "1px solid #f0f0f0" }}>
                <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 700, color: "#6a1b9a", fontFamily: "Consolas,monospace" }}>{it.numero.toLocaleString("it-IT")}</td>
                <td style={{ padding: "4px 8px", fontFamily: "Consolas,monospace", color: "#37474f" }}>{it.data}</td>
                <td style={{ padding: "4px 8px", fontFamily: "Consolas,monospace", color: "#555" }}>{it.plesso}</td>
                <td style={{ padding: "4px 8px", fontWeight: 600 }}>{it.studente}</td>
                <td style={{ padding: "4px 8px", fontFamily: "Consolas,monospace", color: "#666" }}>{it.codice_fiscale}</td>
                <td style={{ padding: "4px 8px" }}>{it.classe_desc}</td>
                <td style={{ padding: "4px 8px", textAlign: "center", fontWeight: 700, color: it.mensa === "M" ? "#1b5e20" : "#888" }}>{it.mensa || "—"}</td>
                <td style={{ padding: "4px 8px", color: it.dieta ? "#e65100" : "#ccc" }}>{it.dieta || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  return (
    <div data-testid={testid}
      style={{ background: "white", border: `2px solid ${color}`, padding: "8px 12px", textAlign: "center", borderRadius: 3 }}>
      <div style={{ fontSize: 16, fontWeight: 800, color, lineHeight: 1.15, fontFamily: "Consolas,monospace" }}>{value}</div>
      <div style={{ fontSize: 9, color: "#666", marginTop: 4, textTransform: "uppercase", fontWeight: 700 }}>{label}</div>
    </div>
  );
}
